import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";

/**
 * One choice in a bn-select. `description` is the form schema's help text for
 * that enum member and is rendered as a second line, because the policy DSL's
 * closed enums (mask classes, claim operators, enforcement modes, the 22 error
 * codes) are meaningless as bare identifiers.
 *
 * A `disabled` option is one the UI must offer for recognition but must not let
 * anyone pick — a DSL field that decodes cleanly and is then never enforced by
 * the pinned build. `disabledReason` is why, and it is shown, not hidden: the
 * product does not claim a capability the build does not have.
 */
export interface BnSelectOption {
  /** The value written into the policy record. */
  value: string;
  /** The human label. Type-ahead matches against this. */
  label: string;
  /** Secondary help line from the form schema. */
  description?: string;
  /** Renders the option unselectable. */
  disabled?: boolean;
  /** Why it cannot be picked. Shown under the label and as a hover tooltip. */
  disabledReason?: string;
}

/**
 * Detail of bn-select's `change` event. `value` is the single selection (or the
 * first of a multiple selection, or "" when nothing is selected) and `values` is
 * always the full selection, so a consumer can bind either shape without
 * branching on `multiple`.
 */
export interface BnSelectChangeDetail {
  value: string;
  values: string[];
}

let uid = 0;

/**
 * bn-select is the kit's listbox. It is deliberately not a styled native
 * `<select>`: a native option cannot carry a help line, cannot say why a value
 * is unavailable, and cannot be styled from the DS tokens across browsers — and
 * every closed enum in the policy editors needs all three. The archetype is
 * `ClaimCheck.op` (`03` §2.3): Sluice does not validate it, so `op: equals`
 * compiles and silently never matches. The form is what makes that impossible,
 * and the help line is what makes the right value obvious.
 *
 * It implements the APG combobox pattern. Closed, the trigger is the combobox
 * and Arrow/Enter/Space/type-ahead open it. Open, arrows and Home/End move
 * `aria-activedescendant` over the options — including disabled ones, so a
 * screen reader reaches the reason rather than skipping a value the user can
 * see — Enter or Space commits, Escape closes and returns focus to the trigger.
 * With `filterable` the popup carries a filter box which becomes the combobox;
 * use it for long catalogues such as the 22 error codes. With `multiple` the
 * popup stays open and each commit toggles one value.
 *
 * Properties:
 * - `options`     — the choices; `BnSelectOption[]`, property-only
 * - `value`       — the selected value in single mode; mirrored on commit
 * - `values`      — the selected values in `multiple` mode; property-only
 * - `multiple`    — toggle instead of replace, and keep the popup open
 * - `placeholder` — trigger text while nothing is selected
 * - `label`       — accessible name, set via the `aria-label` attribute;
 *                   bn-field fills this in from its own label
 * - `disabled`    — blocks interaction
 * - `invalid`     — error styling and `aria-invalid`; reflected, so a page can
 *                   select `bn-select[invalid]`
 * - `filterable`  — adds the filter box; use it above roughly ten options
 * - `filterPlaceholder`, `emptyText` — filter box copy
 *
 * Events:
 * - `change` — `CustomEvent<BnSelectChangeDetail>`, bubbles and composed. Fired
 *   on every commit, including each toggle in `multiple` mode.
 *
 *   <bn-field label="Claim operator" hint="Sluice does not validate this — a wrong op never matches.">
 *     <bn-select
 *       .options=${claimOps}
 *       .value=${check.op}
 *       @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.check.op = e.detail.value)}
 *     ></bn-select>
 *   </bn-field>
 *
 *   <bn-select
 *     aria-label="Error code"
 *     filterable
 *     multiple
 *     .options=${errorCodes}
 *     .values=${filter.codes}
 *     @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.filter.codes = e.detail.values)}
 *   ></bn-select>
 */
@customElement("bn-select")
export class BnSelect extends LitElement {
  /** The choices. Set as a property, never an attribute. */
  @property({ attribute: false }) options: BnSelectOption[] = [];
  /** The selected value in single mode. */
  @property() value = "";
  /** The selected values in `multiple` mode. */
  @property({ attribute: false }) values: string[] = [];
  /** Toggles values instead of replacing them, and keeps the popup open. */
  @property({ type: Boolean }) multiple = false;
  /** Shown on the trigger when nothing is selected. */
  @property() placeholder = "Select…";

  /**
   * Accessible name for the internal combobox. Bound to the `aria-label`
   * attribute so both `<bn-select aria-label="…">` and bn-field's automatic
   * label forwarding reach the real control inside this shadow root.
   */
  @property({ attribute: "aria-label" }) label = "";

  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  /** Adds a filter box to the popup. Use it above ~10 options. */
  @property({ type: Boolean }) filterable = false;
  @property() filterPlaceholder = "Filter…";
  /** Shown in place of the list when the filter matches nothing. */
  @property() emptyText = "No matches.";

  @state() private expanded = false;
  @state() private activeIndex = -1;
  @state() private filter = "";

  @query(".trigger") private triggerEl!: HTMLButtonElement | null;
  @query(".filter") private filterEl!: HTMLInputElement | null;

  private readonly base = `bn-select-${++uid}`;
  private typeBuffer = "";
  private typeTimer = 0;

  static override styles = css`
    :host {
      display: block;
      position: relative;
      font-family: var(--bino-font-sans);
    }
    .trigger {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      width: 100%;
      box-sizing: border-box;
      text-align: left;
      font-family: inherit;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      cursor: pointer;
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    .trigger:hover:not(:disabled) {
      border-color: var(--bino-border-hover);
    }
    .trigger:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .trigger.invalid {
      border-color: var(--bino-error);
    }
    .trigger.invalid:focus-visible {
      box-shadow: 0 0 0 3px var(--bino-error-bg);
    }
    .trigger:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .text {
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .text.empty {
      color: var(--bino-text-secondary);
    }
    .chev {
      flex: none;
      width: 0.625rem;
      height: 0.625rem;
      stroke: currentColor;
      stroke-width: 1.5;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      color: var(--bino-text-secondary);
      transition: transform var(--bino-transition-fast);
    }
    .trigger.open .chev {
      transform: rotate(180deg);
    }

    .popup {
      position: absolute;
      left: 0;
      right: 0;
      top: calc(100% + var(--bino-space-xs));
      z-index: var(--bino-z-dropdown);
      display: flex;
      flex-direction: column;
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      box-shadow: var(--bino-shadow-dropdown);
      overflow: hidden;
    }
    .filterwrap {
      padding: var(--bino-space-xs);
      border-bottom: 1px solid var(--bino-border);
    }
    .filter {
      width: 100%;
      box-sizing: border-box;
      font-family: inherit;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.375rem 0.5rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-subtle);
    }
    .filter::placeholder {
      color: var(--bino-text-secondary);
    }
    .filter:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }

    .list {
      list-style: none;
      margin: 0;
      padding: var(--bino-space-xs);
      max-height: var(--bino-panel-max-height);
      overflow-y: auto;
    }
    .opt {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      padding: 0.375rem 0.5rem;
      border-radius: var(--bino-radius-sm);
      cursor: pointer;
      scroll-margin: var(--bino-space-xs);
    }
    .opt.active {
      background: var(--bino-surface-hover);
    }
    .opt.off {
      cursor: not-allowed;
    }
    .check {
      flex: none;
      width: 0.75rem;
      height: 0.75rem;
      margin-top: 0.25rem;
      stroke: var(--bino-primary);
      stroke-width: 2;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      visibility: hidden;
    }
    .opt.on .check {
      visibility: visible;
    }
    .body {
      min-width: 0;
    }
    .olabel {
      display: block;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      overflow-wrap: anywhere;
    }
    .opt.on .olabel {
      color: var(--bino-active-text);
      font-weight: 600;
    }
    .opt.off .olabel {
      color: var(--bino-text-secondary);
    }
    .odesc,
    .oreason {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin-top: 0.0625rem;
      overflow-wrap: anywhere;
    }
    .oreason {
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: 0.0625rem var(--bino-space-xs);
      margin-top: var(--bino-space-xs);
    }
    .none {
      padding: var(--bino-space-md);
      text-align: center;
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    window.clearTimeout(this.typeTimer);
  }

  /** A click anywhere outside the component closes the popup without committing. */
  private onDocumentPointerDown = (e: Event) => {
    if (!this.expanded) return;
    if (e.composedPath().includes(this)) return;
    this.collapse(false);
  };

  /** The options left after the filter box, in display order. */
  private get shown(): BnSelectOption[] {
    const q = this.filter.trim().toLowerCase();
    if (!q) return this.options;
    return this.options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description ?? "").toLowerCase().includes(q),
    );
  }

  private get selection(): string[] {
    if (this.multiple) return this.values;
    return this.value ? [this.value] : [];
  }

  private optionId(index: number): string {
    return `${this.base}-opt-${index}`;
  }

  private expand() {
    if (this.disabled || this.expanded) return;
    this.expanded = true;
    const chosen = this.selection;
    const list = this.shown;
    const picked = list.findIndex((o) => chosen.includes(o.value));
    const firstUsable = list.findIndex((o) => !o.disabled);
    this.activeIndex = picked >= 0 ? picked : firstUsable;
    void this.updateComplete.then(() => {
      if (this.filterable) this.filterEl?.focus();
      this.revealActive();
    });
  }

  private collapse(restoreFocus = true) {
    if (!this.expanded) return;
    this.expanded = false;
    this.filter = "";
    this.activeIndex = -1;
    this.typeBuffer = "";
    if (restoreFocus) this.triggerEl?.focus();
  }

  private setActive(index: number) {
    const list = this.shown;
    if (list.length === 0) {
      this.activeIndex = -1;
      return;
    }
    this.activeIndex = Math.min(Math.max(index, 0), list.length - 1);
    this.revealActive();
  }

  private revealActive() {
    if (this.activeIndex < 0) return;
    void this.updateComplete.then(() => {
      const el = this.renderRoot.querySelector<HTMLElement>(`#${this.optionId(this.activeIndex)}`);
      el?.scrollIntoView({ block: "nearest" });
    });
  }

  private commit(option: BnSelectOption) {
    if (option.disabled) return;
    if (this.multiple) {
      this.values = this.values.includes(option.value)
        ? this.values.filter((v) => v !== option.value)
        : [...this.values, option.value];
      this.emitChange();
      return;
    }
    this.value = option.value;
    this.emitChange();
    this.collapse();
  }

  private emitChange() {
    const values = this.multiple ? [...this.values] : this.value ? [this.value] : [];
    this.dispatchEvent(
      new CustomEvent<BnSelectChangeDetail>("change", {
        detail: { value: values[0] ?? "", values },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Shared by the trigger and the filter box, because whichever of the two holds
   * focus drives the same listbox.
   */
  private onKeydown(e: KeyboardEvent) {
    const list = this.shown;
    const typing = this.filterable && this.expanded;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!this.expanded) this.expand();
        else this.setActive(this.activeIndex + 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        if (!this.expanded) this.expand();
        else this.setActive(this.activeIndex - 1);
        return;
      case "Home":
        // In the filter box Home/End are caret movement, not list movement.
        if (typing || !this.expanded) return;
        e.preventDefault();
        this.setActive(0);
        return;
      case "End":
        if (typing || !this.expanded) return;
        e.preventDefault();
        this.setActive(list.length - 1);
        return;
      case "Enter":
        e.preventDefault();
        if (!this.expanded) this.expand();
        else if (this.activeIndex >= 0 && list[this.activeIndex]) this.commit(list[this.activeIndex]);
        return;
      case " ":
        if (typing) return;
        e.preventDefault();
        if (!this.expanded) this.expand();
        else if (this.activeIndex >= 0 && list[this.activeIndex]) this.commit(list[this.activeIndex]);
        return;
      case "Escape":
        if (!this.expanded) return;
        e.preventDefault();
        this.collapse();
        return;
      case "Tab":
        this.collapse(false);
        return;
      default:
        if (!this.filterable) {
          this.typeAhead(e);
        } else if (!this.expanded && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          // Typing on a closed filterable select opens it and seeds the filter,
          // so the keystroke is not swallowed on the way to the filter box.
          e.preventDefault();
          this.filter = e.key;
          this.expand();
        }
    }
  }

  /** Native listbox type-ahead. Only when there is no filter box to type into. */
  private typeAhead(e: KeyboardEvent) {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    if (!this.expanded) this.expand();

    window.clearTimeout(this.typeTimer);
    this.typeBuffer += e.key.toLowerCase();
    this.typeTimer = window.setTimeout(() => (this.typeBuffer = ""), 600);

    const list = this.shown;
    if (list.length === 0) return;
    // A one-character buffer steps to the next match so repeated presses cycle;
    // a longer buffer re-matches from the current option.
    const from = this.typeBuffer.length === 1 ? this.activeIndex + 1 : Math.max(this.activeIndex, 0);
    for (let i = 0; i < list.length; i++) {
      const at = (from + i + list.length) % list.length;
      const option = list[at];
      if (option && option.label.toLowerCase().startsWith(this.typeBuffer)) {
        this.setActive(at);
        return;
      }
    }
  }

  private onFilterInput(e: Event) {
    // The native `input` event is composed and would otherwise escape the shadow
    // root and reach the page's own handler as if the selection had changed.
    e.stopPropagation();
    this.filter = (e.target as HTMLInputElement).value;
    const list = this.shown;
    const firstUsable = list.findIndex((o) => !o.disabled);
    this.activeIndex = firstUsable >= 0 ? firstUsable : list.length > 0 ? 0 : -1;
    this.revealActive();
  }

  private triggerText(): string {
    const chosen = this.selection;
    if (chosen.length === 0) return this.placeholder;
    const labels = chosen.map((v) => this.options.find((o) => o.value === v)?.label ?? v);
    if (labels.length === 1) return labels[0] ?? this.placeholder;
    return `${labels.length} selected`;
  }

  override render() {
    const list = this.shown;
    const chosen = this.selection;
    const listId = `${this.base}-list`;
    const active = this.expanded && this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
    const name = this.label || this.placeholder;
    const empty = chosen.length === 0;

    return html`
      <button
        class="trigger ${this.invalid ? "invalid" : ""} ${this.expanded ? "open" : ""}"
        type="button"
        role=${this.filterable ? nothing : "combobox"}
        aria-haspopup="listbox"
        aria-expanded=${this.expanded ? "true" : "false"}
        aria-controls=${this.expanded ? listId : nothing}
        aria-activedescendant=${!this.filterable && active ? active : nothing}
        aria-label=${name}
        aria-invalid=${this.invalid ? "true" : nothing}
        ?disabled=${this.disabled}
        @click=${() => (this.expanded ? this.collapse() : this.expand())}
        @keydown=${this.onKeydown}
      >
        <span class="text ${empty ? "empty" : ""}">${this.triggerText()}</span>
        <svg class="chev" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4"></path>
        </svg>
      </button>
      ${this.expanded
        ? html`
            <div class="popup" @mousedown=${(e: Event) => e.preventDefault()}>
              ${this.filterable
                ? html`
                    <div class="filterwrap">
                      <input
                        class="filter"
                        type="text"
                        role="combobox"
                        autocomplete="off"
                        spellcheck="false"
                        aria-expanded="true"
                        aria-controls=${listId}
                        aria-autocomplete="list"
                        aria-activedescendant=${active ?? nothing}
                        aria-label=${`Filter ${name}`}
                        placeholder=${this.filterPlaceholder}
                        .value=${this.filter}
                        @input=${this.onFilterInput}
                        @keydown=${this.onKeydown}
                      />
                    </div>
                  `
                : nothing}
              <ul
                class="list"
                id=${listId}
                role="listbox"
                aria-label=${name}
                aria-multiselectable=${this.multiple ? "true" : nothing}
              >
                ${list.map((o, i) => {
                  const on = chosen.includes(o.value);
                  return html`
                    <li
                      class="opt ${i === this.activeIndex ? "active" : ""} ${on ? "on" : ""} ${o.disabled
                        ? "off"
                        : ""}"
                      id=${this.optionId(i)}
                      role="option"
                      aria-selected=${on ? "true" : "false"}
                      aria-disabled=${o.disabled ? "true" : nothing}
                      title=${o.disabled && o.disabledReason ? o.disabledReason : nothing}
                      @click=${() => this.commit(o)}
                      @mousemove=${() => this.setActive(i)}
                    >
                      <svg class="check" viewBox="0 0 12 12" aria-hidden="true">
                        <path d="M2.5 6.5 5 9l4.5-6"></path>
                      </svg>
                      <span class="body">
                        <span class="olabel">${o.label}</span>
                        ${o.description ? html`<span class="odesc">${o.description}</span>` : nothing}
                        ${o.disabled && o.disabledReason
                          ? html`<span class="oreason">${o.disabledReason}</span>`
                          : nothing}
                      </span>
                    </li>
                  `;
                })}
              </ul>
              ${list.length === 0 ? html`<div class="none">${this.emptyText}</div>` : nothing}
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-select": BnSelect;
  }
}
